import React, { SyntheticEvent, useCallback, useState } from "react";
import { connect } from "react-redux";

import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button } from "@mapmycustomers/ui";

import UserSetting from "@app/enum/UserSetting";
import { getCurrentUserId, getOrganizationId, getUserSetting } from "@app/store/iam";
import { updateSetting } from "@app/store/iam/actions";
import { deleteNotification, hideNotifications } from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import MmcNotification from "@app/types/MmcNotification";

import styles from "./Actions.module.scss";
import optOutSetting from "./util/optOutSetting";
import useNotificationMenu from "./util/useNotificationMenu";

interface Props {
  currentUserId: User["id"];
  deleteNotification: typeof deleteNotification.request;
  hideNotifications: typeof hideNotifications;
  hovered: boolean;
  notification: MmcNotification;
  onGoToSettings: () => void;
  organizationId: Organization["id"];
  setting: Setting;
  updateSetting: typeof updateSetting.request;
}

const NotificationMenu = ({
  currentUserId,
  deleteNotification,
  hideNotifications,
  hovered,
  notification,
  onGoToSettings,
  organizationId,
  setting,
  updateSetting,
}: Props) => {
  const intl = useIntl();
  const [menuVisible, setMenuVisibility] = useState<boolean>(false);

  const handleClearNotification = useCallback(
    ({ domEvent }: { domEvent: SyntheticEvent<HTMLElement> }) => {
      stopEvents(domEvent);
      setMenuVisibility(false);
      deleteNotification({ id: notification.id });
    },
    [deleteNotification, notification.id, setMenuVisibility]
  );

  const handleOptOut = useCallback(
    ({ domEvent }: { domEvent: SyntheticEvent<HTMLElement> }) => {
      stopEvents(domEvent);
      hideNotifications();
      setMenuVisibility(false);
      optOutSetting(
        intl,
        updateSetting,
        organizationId,
        currentUserId,
        setting,
        notification.action,
        onGoToSettings
      );
    },
    [
      hideNotifications,
      intl,
      currentUserId,
      notification.action,
      onGoToSettings,
      organizationId,
      setMenuVisibility,
      setting,
      updateSetting,
    ]
  );

  const handleGoToSettings = useCallback(
    ({ domEvent }: { domEvent: SyntheticEvent<HTMLElement> }) => {
      stopEvents(domEvent);
      setMenuVisibility(false);
      onGoToSettings();
    },
    [onGoToSettings, setMenuVisibility]
  );

  const notificationOptions = useNotificationMenu(
    intl,
    setting,
    notification.action,
    handleClearNotification,
    handleOptOut,
    handleGoToSettings
  );

  const handleClickMenuButton = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      stopEvents(event);
      setMenuVisibility((visibility) => !visibility);
    },
    [setMenuVisibility]
  );

  return (
    <Dropdown
      dropdownRender={() => notificationOptions}
      onOpenChange={setMenuVisibility}
      open={menuVisible}
      placement="bottomRight"
      trigger={["click"]}
    >
      <Tooltip
        title={intl.formatMessage({
          id: "navbar.notifications.list.notification.more",
          defaultMessage: "More",
          description: "Navbar Notifications - list - notification - more",
        })}
      >
        <Button
          hidden={!hovered && !menuVisible}
          icon={<FontAwesomeIcon className={styles.moreIcon} icon={faEllipsisV} />}
          onClick={handleClickMenuButton}
          type="link"
        />
      </Tooltip>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  organizationId: getOrganizationId(state)!,
  setting: getUserSetting(state, UserSetting.EMAIL_NOTIFICATION_SETTINGS)!,
});

const mapDispatchToProps = {
  deleteNotification: deleteNotification.request,
  hideNotifications,
  updateSetting: updateSetting.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationMenu);
